/* Phase 2E-lite — Safe Social Profile Registry UI.
 *
 * Honesty-first design (Section 8 of the spec):
 *  - Every row shows source label + analysis status pills.
 *  - Empty state explicitly tells the user: "Profile discovered/provided.
 *    Content not deeply analyzed yet."
 *  - Confirm / Reject buttons per row.
 *  - Prompt: "Upload screenshots/exports later if you want content analysis."
 *
 * NEVER scrapes, NEVER does OAuth. Backend never fetches social content.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Globe, Instagram, Facebook, Linkedin, Youtube, RefreshCw,
    CheckCircle2, XCircle, Sparkles, AlertCircle, Upload
} from "lucide-react";

const PLATFORM_ICON = {
    instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
    youtube: Youtube, x: Sparkles, tiktok: Sparkles,
    google_business_profile: Globe, other: Globe,
};

const SOURCE_LABEL_TONE = {
    user_provided:                  "border-zinc-700 text-zinc-300 bg-zinc-900/40",
    website_verified_social_link:   "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    unverified_candidate:           "border-amber-500/30 text-amber-300 bg-amber-500/5",
    ai_inference:                   "border-violet-500/30 text-violet-300 bg-violet-500/5",
    library_based:                  "border-sky-500/30 text-sky-300 bg-sky-500/5",
};

const VERIFICATION_TONE = {
    confirmed:                  "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
    candidate:                  "border-amber-500/40 text-amber-200 bg-amber-500/10",
    rejected:                   "border-red-500/40 text-red-200 bg-red-500/10",
    needs_manual_verification:  "border-zinc-700 text-zinc-300 bg-zinc-900/40",
};

function prettyLabel(s) {
    return (s || "").replace(/_/g, " ");
}

export default function SocialProfilesPanel({ clientId = null, projectId = null }) {
    const scope = clientId ? "client" : "project";
    const baseUrl = clientId
        ? `/clients/${clientId}/social-profiles`
        : `/projects/${projectId}/social-profiles`;

    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [honesty, setHonesty] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(baseUrl);
            setProfiles(data.profiles || []);
            setHonesty(data.honesty_notice || "");
        } catch (e) {
            toast.error(e.userMessage || "Could not load social profiles");
        } finally { setLoading(false); }
    }, [baseUrl]);

    useEffect(() => { load(); }, [load]);

    const sync = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`${baseUrl}/sync`);
            toast.success(`Synced ${data.synced} profile(s) from ${scope} fields.`);
            await load();
        } catch (e) {
            toast.error(e.userMessage || "Sync failed");
        } finally { setBusy(false); }
    };

    const discoverFromWebsite = async () => {
        if (!clientId) return;
        setBusy(true);
        try {
            const { data } = await api.post(
                `/clients/${clientId}/social-profiles/discover-from-website`
            );
            toast.success(
                `Discovered ${data.discovered} profile link(s) from website research.`
            );
            await load();
        } catch (e) {
            toast.error(e.userMessage || "Discovery failed");
        } finally { setBusy(false); }
    };

    const confirm = async (id) => {
        try {
            await api.patch(`/social-profiles/${id}/confirm`);
            toast.success("Profile confirmed.");
            await load();
        } catch (e) { toast.error(e.userMessage || "Could not confirm"); }
    };

    const reject = async (id) => {
        try {
            await api.patch(`/social-profiles/${id}/reject`);
            toast.success("Profile rejected — removed from this view.");
            await load();
        } catch (e) { toast.error(e.userMessage || "Could not reject"); }
    };

    return (
        <div className="card-base p-5" data-testid={`social-profiles-panel-${scope}`}>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Globe className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Social profiles</h3>
                    <span className="text-xs text-zinc-500">{profiles.length} on file</span>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button" onClick={sync} disabled={busy}
                        data-testid="social-sync-btn"
                        className="btn-secondary text-xs disabled:opacity-50"
                        title={`Pull social URLs from ${scope} fields into the registry.`}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                        Sync from {scope}
                    </button>
                    {clientId && (
                        <button
                            type="button" onClick={discoverFromWebsite} disabled={busy}
                            data-testid="social-discover-btn"
                            className="btn-secondary text-xs disabled:opacity-50"
                            title="Mine cached website research for social profile links. No new network calls."
                        >
                            <Sparkles className="w-3.5 h-3.5" />
                            Discover from website
                        </button>
                    )}
                </div>
            </div>

            <div className="text-xs text-zinc-500 mb-4 leading-relaxed border-l-2 border-amber-500/30 pl-3 bg-amber-500/[0.03] py-2 rounded-r-sm"
                 data-testid="social-honesty-notice">
                <span className="text-amber-300 font-medium">Content not deeply analyzed yet.</span>{" "}
                {honesty || "Furmais stores profile URLs only in this phase. No platform login, OAuth, or scraping. Upload screenshots / exports later if you want content analysis."}
            </div>

            {loading && <div className="text-xs text-zinc-500">Loading…</div>}
            {!loading && profiles.length === 0 && (
                <div className="text-xs text-zinc-500 italic">
                    No social profiles yet. Add Instagram / Facebook / LinkedIn / YouTube URLs
                    in the {scope} edit page, then click <span className="text-zinc-300">Sync from {scope}</span>.
                </div>
            )}

            <div className="space-y-2">
                {profiles.map(p => (
                    <ProfileRow key={p.id} p={p}
                                onConfirm={() => confirm(p.id)}
                                onReject={() => reject(p.id)} />
                ))}
            </div>
        </div>
    );
}

function ProfileRow({ p, onConfirm, onReject }) {
    const Icon = PLATFORM_ICON[p.platform] || Globe;
    const sourceClass = SOURCE_LABEL_TONE[p.source_label] || SOURCE_LABEL_TONE.user_provided;
    const verifClass  = VERIFICATION_TONE[p.verification_status] || VERIFICATION_TONE.needs_manual_verification;
    return (
        <div
            className="border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-2"
            data-testid={`social-profile-${p.id}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {prettyLabel(p.platform)}
                    </span>
                    <a href={p.profile_url} target="_blank" rel="noreferrer"
                       className="text-sm text-zinc-200 hover:underline truncate">
                        {p.handle || p.profile_url}
                    </a>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${sourceClass}`}
                        data-testid={`social-source-label-${p.id}`}
                    >
                        {prettyLabel(p.source_label)}
                    </span>
                    <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${verifClass}`}
                        data-testid={`social-verification-${p.id}`}
                    >
                        {prettyLabel(p.verification_status)}
                    </span>
                </div>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
                <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" />
                    <span data-testid={`social-analysis-status-${p.id}`}>
                        {p.analysis_status === "profile_confirmed"
                            ? "Profile confirmed — content not deeply analyzed yet."
                            : p.analysis_status === "content_not_analyzed"
                            ? "Profile discovered/provided. Content not deeply analyzed yet."
                            : prettyLabel(p.analysis_status)}
                    </span>
                </div>
                {p.verification_status !== "confirmed" && (
                    <div className="flex gap-1.5 shrink-0">
                        <button
                            type="button" onClick={onConfirm}
                            data-testid={`social-confirm-${p.id}`}
                            className="text-xs px-2 py-1 rounded-sm border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                        >
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />
                            Confirm
                        </button>
                        <button
                            type="button" onClick={onReject}
                            data-testid={`social-reject-${p.id}`}
                            className="text-xs px-2 py-1 rounded-sm border border-red-500/30 text-red-300 hover:bg-red-500/10"
                        >
                            <XCircle className="w-3 h-3 inline mr-1" />
                            Reject
                        </button>
                    </div>
                )}
            </div>
            <div className="mt-1 text-[10px] text-zinc-600 flex items-center gap-1.5">
                <Upload className="w-2.5 h-2.5" />
                Upload screenshots/exports later if you want content analysis.
            </div>
        </div>
    );
}
